import {
  AutomaticSavingsFeaturesResultTypenames,
  Mutation,
  MutationUpdateSypArgs,
  Query,
  UpdateSypError,
  UpdateSYPResultTypenames,
} from '@varo/graphql-schema-types';
import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { UpdateSYP } from 'services/graphql/mutations/UpdateSYP';
import GetAutomaticSavings from 'services/graphql/queries/GetAutomaticSavings';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import NumberPad from 'shared/components/vxl/NumberPad/NumberPad';
import Snackbar from 'shared/components/vxl/Snackbar/Snackbar';
import ExpressiveXXLText from 'shared/components/vxl/typography/expressive/ExpressiveXXLText';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import { useAutoHideSnackbar } from 'shared/hooks/snackBar/useAutoHideSnackbar';
import useCancellableMutation, {
  AbortError,
} from 'shared/hooks/useCancellableMutation';
import { useHandleKeypadButtonPress } from 'shared/hooks/useHandleKeypadButtonPress';
import DefaultPageDownErrorMessage from 'shared/patterns/vxl/errors/PageDownErrorMessage/DefaultPageDownErrorMessage';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';
import { useQuery } from 'urql';

const SaveYourPay = () => {
  const router = useRouter();
  const { bottom } = useSafeAreaInsets();
  const [{ data, fetching, error }, refetchBaseQuery] = useQuery<
    Pick<Query, 'user'>
  >({
    query: GetAutomaticSavings,
    requestPolicy: 'network-only',
  });
  const { defaultMessage, defaultTitle } = useDefaultErrorStrings();
  const automaticSavingsResult = data?.user?.customerInfo?.automaticSavings;
  const automaticSavings =
    automaticSavingsResult?.__typename ===
    AutomaticSavingsFeaturesResultTypenames.AutomaticSavingsFeaturesEnabled
      ? automaticSavingsResult
      : undefined;
  const percentage = automaticSavings?.syp.percentage.toString() ?? '';
  const automaticMYPSavingsPercentage =
    data?.user?.customerInfo?.myp?.percentage?.toString() ?? '';
  const wholeNumberPercentage = `${Math.round(parseFloat(percentage)) || 0}%`;
  const automaticMYPSavingsPercentageValue =
    Math.round(parseFloat(automaticMYPSavingsPercentage)) || 0;
  const snackbarDuration = 2750;
  const [snackbar, setSnackbar] = useState<{ message: string }>({
    message: '',
  });
  const [updateSYPResponseError, setUpdateSYPResponseError] =
    useState<UpdateSypError | null>(null);
  const [missingUpdateSYPResponseError, setMissingUpdateSYPResponseError] =
    useState<Error | null>(null);
  const { updatedSYPPercentage, handleKeypadButtonPress } =
    useHandleKeypadButtonPress(wholeNumberPercentage);
  const { t: translatedText } = useTranslation('accounts', {
    keyPrefix: 'saveYourPay',
  });
  const [{ fetching: updateSYPFetching, error: updateSYPError }, updateSYP] =
    useCancellableMutation<Pick<Mutation, 'updateSYP'>, MutationUpdateSypArgs>(
      UpdateSYP,
    );
  const updateSYPFailure = updateSYPFetching
    ? null
    : (updateSYPError ??
      updateSYPResponseError ??
      missingUpdateSYPResponseError);
  const screenError = error ?? updateSYPFailure;

  useViewDidFocusScreenAnalyticsEvent({
    key: 'Automatic Savings SYP Input',
  });

  const sypUpdatePercentageEvent = usePressableAnalytics({
    analyticsId: 'sypUpdatePercentage',
    customAttributes: {
      syp_percentage: updatedSYPPercentage,
    },
  });

  useAutoHideSnackbar(
    Boolean(snackbar.message),
    () => {
      setSnackbar({ message: '' });
    },
    snackbarDuration,
  );

  // Percentage can't be greater than 100%.
  const isSubmitButtonDisabled =
    updatedSYPPercentage === wholeNumberPercentage ||
    parseFloat(updatedSYPPercentage) > 100 - automaticMYPSavingsPercentageValue;

  const percentLimitMessage = translatedText('percentLimitMessage', {
    automaticMYPSavingsPercentage: 100 - automaticMYPSavingsPercentageValue,
  });

  const handleUpdateSYP = useCallback(async () => {
    sypUpdatePercentageEvent();
    setUpdateSYPResponseError(null);
    setMissingUpdateSYPResponseError(null);

    try {
      const response = await updateSYP({
        input: {
          percentage: updatedSYPPercentage,
        },
      });

      if (response.error) {
        return;
      }

      const updateSYPResult = response.data?.updateSYP;
      if (
        updateSYPResult?.__typename === UpdateSYPResultTypenames.UpdateSYPError
      ) {
        setUpdateSYPResponseError(updateSYPResult);
        return;
      }

      if (
        updateSYPResult?.__typename !==
        UpdateSYPResultTypenames.UpdateSYPSuccess
      ) {
        setMissingUpdateSYPResponseError(new Error(defaultMessage));
        return;
      }

      if (
        'successMessage' in updateSYPResult &&
        typeof updateSYPResult.successMessage === 'string' &&
        updateSYPResult.successMessage
      ) {
        setSnackbar({
          message: updateSYPResult.successMessage,
        });
      }

      if (router.canGoBack()) {
        router.back();
      } else {
        router.replace('/accounts/automatic-savings');
      }
    } catch (mutationError) {
      if (mutationError instanceof AbortError) {
        return;
      }

      throw mutationError;
    }
  }, [
    defaultMessage,
    router,
    sypUpdatePercentageEvent,
    updateSYP,
    updatedSYPPercentage,
  ]);

  const handleTryAgain = () => {
    if (error) {
      refetchBaseQuery();
      return;
    }

    void handleUpdateSYP();
  };

  return (
    <View style={styles.screenContainer}>
      <Stack.Screen
        options={{
          title: translatedText('headerTitle'),
          headerShown: true,
          headerBackVisible: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      {snackbar.message ? (
        <Snackbar duration={snackbarDuration} text={snackbar.message} />
      ) : null}
      <ScrollView
        contentContainerStyle={[styles.flex, { paddingBottom: bottom }]}
      >
        <View style={styles.inputContainer}>
          <BodyBaseText>{translatedText('description')}</BodyBaseText>
          <View style={styles.percentageMainWrapper}>
            <ExpressiveXXLText style={styles.updatedSYPPercentage}>
              {updatedSYPPercentage}
            </ExpressiveXXLText>
          </View>
          <BodyXSText
            style={
              parseFloat(updatedSYPPercentage) <=
              100 - automaticMYPSavingsPercentageValue
                ? styles.percentLimitMessage
                : styles.percentLimitMessageError
            }
          >
            {percentLimitMessage}
          </BodyXSText>
        </View>
        <View>
          <NumberPad
            handleButtonPress={handleKeypadButtonPress}
            handleSubmit={handleUpdateSYP}
            submitButtonLabel={translatedText('updateButtonLabel')}
            submitDisabled={isSubmitButtonDisabled}
            submitButtonLoading={updateSYPFetching}
            disabled={updateSYPFetching}
          />
        </View>
      </ScrollView>
      <ScreenTransitionOverlay
        isLoading={fetching}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={
          screenError ? (
            <DefaultPageDownErrorMessage
              error={screenError}
              title={updateSYPResponseError?.title ?? defaultTitle}
              message={screenError.message || defaultMessage}
              tryAgainAction={handleTryAgain}
            />
          ) : null
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  flex: {
    flex: 1,
  },
  inputContainer: {
    flex: 1,
    minHeight: 130,
  },
  percentLimitMessage: {
    color: theme.color.text.secondary,
    textAlign: 'center',
  },
  percentLimitMessageError: {
    color: theme.color.status.negative,
    textAlign: 'center',
  },
  percentageMainWrapper: {
    flex: 1,
    justifyContent: 'center',
  },
  screenContainer: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    padding: theme.spacing.m,
  },
  updatedSYPPercentage: {
    textAlign: 'center',
  },
});

export default SaveYourPay;
